import { devError } from '@/lib/logger';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, LogIn, Coffee, LogOut, ArrowLeft, Camera, MapPin, Shield, Printer } from 'lucide-react';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { Toaster } from '@/components/ui/sonner';
import { PinLoginPanel, ActionPanel } from '@/components/KioskPanels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kiosk() {
  const { locationId: paramLocationId } = useParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const logoutTimerRef = useRef(null);
  
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(paramLocationId || '');
  const [locationName, setLocationName] = useState('');
  const [recognizedEmployee, setRecognizedEmployee] = useState(null);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [settings, setSettings] = useState({ test_mode: false });
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [rgpdDialogOpen, setRgpdDialogOpen] = useState(false);
  const [rgpdText, setRgpdText] = useState('');
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [userGeo, setUserGeo] = useState(null);
  const [geoStatus, setGeoStatus] = useState('pending');
  const [loginMethod, setLoginMethod] = useState(null); // 'pin'
  const [capturing, setCapturing] = useState(false); // true while camera is on
  const [actionLocked, setActionLocked] = useState(false);
  const [captureStage, setCaptureStage] = useState('idle'); // 'warmup' | 'countdown' | 'snap'
  const [captureCountdown, setCaptureCountdown] = useState(0);
  const [captureFlash, setCaptureFlash] = useState(false);

  // Geo on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setUserGeo({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGeoStatus('ok'); },
        () => setGeoStatus('denied'),
        { enableHighAccuracy: true }
      );
    } else { setGeoStatus('error'); }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchSettings();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedLocation) {
      setLocationName(locations.find(l => l.location_id === selectedLocation)?.name || '');
      setRecognizedEmployee(null); setCurrentRecord(null);
      setPinInput(''); setPinError('');
    }
  }, [selectedLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLocations = async () => {
    try { const r = await fetch(`${API}/locations`); const d = await r.json(); setLocations(d);
      if (paramLocationId) setSelectedLocation(paramLocationId); else if (d.length > 0 && !selectedLocation) setSelectedLocation(d[0].location_id);
    } catch (err) { devError('kiosk-loc', err); }
  };
  const fetchSettings = async () => {
    try { const r = await fetch(`${API}/settings/public`); if (r.ok) setSettings(await r.json()); } catch (err) { devError('kiosk-set', err); }
  };

  // Auth flow
  const handlePinLogin = async () => {
    setPinError('');
    if (!pinInput || pinInput.length < 4) {
      setPinError('PIN deve ter 4 a 6 dígitos');
      return;
    }
    if (!selectedLocation) {
      setPinError('Selecione um local');
      return;
    }
    try {
      const r = await fetch(`${API}/kiosk/pin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput, location_id: selectedLocation }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 409) {
          setPinError('PIN duplicado neste local. Contacte o gestor.');
        } else {
          setPinError(data.detail || 'PIN inválido');
        }
        setPinInput('');
        return;
      }
      setLoginMethod('pin');
      await onEmployeeIdentified(data.employee);
    } catch (err) {
      devError('pin-login', err);
      setPinError('Erro de ligação. Tente novamente.');
    }
  };

  const onEmployeeIdentified = async (emp) => {
    try {
      const r = await fetch(`${API}/rgpd/check/${emp.employee_id}`);
      const d = await r.json();
      if (!d.accepted) {
        setPendingEmployee(emp);
        const tr = await fetch(`${API}/rgpd/text`);
        setRgpdText((await tr.json()).text);
        setRgpdDialogOpen(true);
        return;
      }
    } catch (err) { devError('rgpd-check', err); }
    await activateEmployee(emp);
  };

  const acceptRgpd = async () => {
    if (!pendingEmployee) return;
    try {
      await fetch(`${API}/rgpd/accept`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ employee_id: pendingEmployee.employee_id }) });
      toast.success('Consentimento RGPD aceite');
      setRgpdDialogOpen(false);
      await activateEmployee(pendingEmployee);
      setPendingEmployee(null);
    } catch (err) { devError('rgpd-accept', err); toast.error('Erro ao aceitar RGPD'); }
  };

  const printRgpd = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const pre = win.document.createElement('pre');
    pre.style.cssText = 'font-family:monospace;white-space:pre-wrap;max-width:800px;margin:40px auto;font-size:14px;';
    pre.textContent = `${rgpdText}\n\nFuncionário: ${pendingEmployee?.name || ''}\nEmail: ${pendingEmployee?.email || ''}`;
    win.document.body.appendChild(pre);
    win.document.close();
    win.print();
  };

  const activateEmployee = async (emp) => {
    setRecognizedEmployee(emp);
    // Look across yesterday + today so night shifts that cross midnight
    // (e.g. clock-in Mon 18:00, clock-out Tue 01:30) are found correctly.
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    try {
      const r = await fetch(`${API}/time-records?employee_id=${emp.employee_id}&start_date=${yesterday}&end_date=${today}`);
      const recs = await r.json();
      const list = Array.isArray(recs) ? recs : [];
      // 1) ANY open record (working / on_break) — even from yesterday — so the
      //    employee can close it.
      const openRec = list.find(x => x.status === 'working' || x.status === 'on_break');
      // 2) Otherwise, today's record only. Yesterday's COMPLETED record must
      //    NEVER be set as currentRecord, otherwise Entrada button stays
      //    disabled today.
      const todayRec = list.find(x => x.date === today);
      setCurrentRecord(openRec || todayRec || null);
    }
    catch (err) { devError('kiosk-rec', err); }
    toast.success(`Bem-vindo, ${emp.name}!`);
  };

  // Time actions: camera turns ON only at capture time, then OFF, then optional auto-logout
  const recordAction = async (action) => {
    if (!recognizedEmployee || actionLocked) return;
    setActionLocked(true);

    // 1) Mount camera in WARMUP stage (let stream stabilize)
    setCaptureStage('warmup');
    setCapturing(true);
    await new Promise(r => setTimeout(r, 1500));
    for (let i = 0; i < 8; i += 1) {
      const v = webcamRef.current?.video;
      if (v && v.readyState === 4) break;
      await new Promise(r => setTimeout(r, 200));
    }

    // 2) COUNTDOWN 3-2-1
    setCaptureStage('countdown');
    for (let n = 3; n >= 1; n -= 1) {
      setCaptureCountdown(n);
      await new Promise(r => setTimeout(r, 1000));
    }
    setCaptureCountdown(0);

    // 3) SNAP — try a few times in case the frame is empty
    let photo = null;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      photo = webcamRef.current?.getScreenshot();
      if (photo && photo.length > 100) break;
      await new Promise(r => setTimeout(r, 200));
    }

    // 4) Show captured frame for 1.6s with quick flash
    setCaptureStage('snap');
    setCaptureFlash(true);
    await new Promise(r => setTimeout(r, 200));
    setCaptureFlash(false);
    await new Promise(r => setTimeout(r, 1400));

    // 5) Turn camera OFF (unmount)
    setCapturing(false);
    setCaptureStage('idle');

    if (!photo) toast.warning('Câmera indisponível — ponto registado sem foto');

    // 6) Send the action with the captured photo
    try {
      const r = await fetch(`${API}/time-records/action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: recognizedEmployee.employee_id, location_id: selectedLocation, action, photo: photo || undefined, latitude: userGeo?.lat, longitude: userGeo?.lon })
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      const rec = await r.json();
      setCurrentRecord(rec);
      const labels = { clock_in: 'Entrada', break_start: 'Pausa', break_end: 'Retorno', clock_out: 'Saída' };
      toast.success(`${labels[action]} registrada${photo ? ' (foto capturada)' : ''}`);

      // 7) Auto-logout after PIN-based picagem OR after clock_out (any flow)
      if (loginMethod === 'pin' || action === 'clock_out') {
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = setTimeout(() => resetKiosk(), 2500);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLocked(false);
    }
  };

  const resetKiosk = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    setRecognizedEmployee(null);
    setCurrentRecord(null);
    setPinInput('');
    setPinError('');
    setLoginMethod(null);
    setCapturing(false);
    setCaptureStage('idle');
    setCaptureCountdown(0);
  };

  const actionButtons = [
    { action: 'clock_in', label: 'Entrada', icon: <LogIn className="w-6 h-6" />, enabled: !currentRecord || currentRecord.status === 'not_started' || currentRecord.status === 'completed', color: 'bg-[#059669]' },
    { action: 'break_start', label: 'Início Pausa', icon: <Coffee className="w-6 h-6" />, enabled: currentRecord?.status === 'working', color: 'bg-[#F59E0B]' },
    { action: 'break_end', label: 'Fim Pausa', icon: <Coffee className="w-6 h-6" />, enabled: currentRecord?.status === 'on_break', color: 'bg-[#F59E0B]' },
    { action: 'clock_out', label: 'Saída', icon: <LogOut className="w-6 h-6" />, enabled: currentRecord?.status === 'working', color: 'bg-[#DC2626]' },
  ];

  const fmtTime = (iso) => { if (!iso) return '--:--'; try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return '--:--'; } };

  return (
    <div className="min-h-screen bg-[#111111]">
      <Toaster position="top-center" />
      {settings.test_mode && <div className="test-mode-banner h-8 flex items-center justify-center"><span className="text-xs font-bold uppercase tracking-widest text-white bg-[#111111] px-3 py-1">MODO TESTE</span></div>}

      <header className="bg-[#111111] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate('/')}><ArrowLeft className="w-5 h-5" /></Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0029FF] flex items-center justify-center"><Clock className="w-6 h-6 text-white" /></div>
              <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Satoshi, sans-serif' }}>TimeTrack Bio</span>
            </div>
            {locations.length > 0 && (
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-52 bg-white/10 border-white/20 text-white ml-4" data-testid="kiosk-location-select"><SelectValue placeholder="Selecione o local" /></SelectTrigger>
                <SelectContent>{locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="text-right text-white flex items-center gap-4">
            <div className={`flex items-center gap-1 text-xs ${geoStatus === 'ok' ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
              <MapPin className="w-4 h-4" /> {geoStatus === 'ok' ? 'GPS Ativo' : geoStatus === 'denied' ? 'GPS Negado' : 'GPS...'}
            </div>
            <div>
              <div className="text-4xl font-black mono" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
              <div className="text-sm text-white/60">{currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="kiosk-layout" style={{ minHeight: 'calc(100vh - 73px)' }}>
        <div className="kiosk-camera p-8">
          {!selectedLocation ? (
            <p className="text-white/60 text-lg text-center">Selecione um local no menu acima</p>
          ) : recognizedEmployee ? (
            <div className="text-center w-full">
              <div className="relative w-full max-w-md aspect-[4/3] bg-black rounded overflow-hidden mx-auto mb-4" data-testid="kiosk-camera-frame">
                {capturing ? (
                  <>
                    <Webcam ref={webcamRef} audio={false} className="w-full h-full object-cover" mirrored screenshotFormat="image/jpeg" screenshotQuality={0.6} />

                    {/* Top-right state badge */}
                    <div
                      className={`absolute top-3 right-3 text-white text-xs px-3 py-1.5 font-bold uppercase tracking-wider flex items-center gap-1 ${
                        captureStage === 'snap' ? 'bg-[#059669]' : 'bg-[#DC2626] animate-pulse'
                      }`}
                      data-testid="kiosk-rec-indicator"
                    >
                      <Camera className="w-3 h-3" />
                      {captureStage === 'warmup' && 'A LIGAR'}
                      {captureStage === 'countdown' && 'PREPARAR'}
                      {captureStage === 'snap' && 'OK CAPTURADO'}
                    </div>

                    {/* Countdown overlay */}
                    {captureStage === 'countdown' && captureCountdown > 0 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none" data-testid="kiosk-countdown">
                        <div className="text-white font-black drop-shadow-2xl" style={{ fontFamily: 'Satoshi, sans-serif', fontSize: '180px', lineHeight: 1 }}>
                          {captureCountdown}
                        </div>
                      </div>
                    )}

                    {/* Flash effect right after snap */}
                    {captureFlash && (
                      <div className="absolute inset-0 bg-white pointer-events-none" data-testid="kiosk-flash" />
                    )}

                    {/* Bottom instruction bar */}
                    <div className="absolute top-3 left-3 bg-black/70 text-white/90 text-[11px] px-3 py-1.5 uppercase tracking-wider">
                      {captureStage === 'warmup' && 'A ligar a câmera...'}
                      {captureStage === 'countdown' && 'Posicione-se em frente à câmera'}
                      {captureStage === 'snap' && '✓ Foto registada'}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/50" data-testid="kiosk-camera-off">
                    <Camera className="w-16 h-16 mb-3 opacity-40" />
                    <p className="text-sm uppercase tracking-widest">Câmera desligada</p>
                    <p className="text-xs mt-1 opacity-60">Liga ao clicar numa ação</p>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-4 py-2 text-white text-left">
                  <p className="font-bold text-lg">{recognizedEmployee.name}</p>
                  <p className="text-xs text-white/60">{recognizedEmployee.email}</p>
                </div>
              </div>
              {currentRecord && (
                <div className="grid grid-cols-4 gap-3 max-w-md mx-auto text-white/80">
                  {[['Entrada','clock_in'],['Pausa','break_start'],['Retorno','break_end'],['Saída','clock_out']].map(([label,key]) => (
                    <div key={key} className="text-center"><span className="text-[10px] uppercase tracking-widest text-white/40 block">{label}</span><span className="text-sm mono font-bold">{fmtTime(currentRecord[key])}</span></div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="w-full max-w-lg text-center" data-testid="kiosk-login-frame">
              <div className="relative w-full aspect-[4/3] bg-black rounded overflow-hidden flex flex-col items-center justify-center text-white/50">
                <Camera className="w-20 h-20 mb-4 opacity-40" />
                <p className="text-sm uppercase tracking-widest">Câmera desligada</p>
                <p className="text-xs mt-1 opacity-60">Liga só na picagem do ponto</p>
              </div>
              <p className="text-white/60 mt-4">{locationName && <span className="block text-white font-semibold mb-2">{locationName}</span>}Identifique-se com email e PIN</p>
            </div>
          )}
        </div>

        <div className="kiosk-actions bg-[#FAFAFA]">
          {!selectedLocation ? (
            <div className="flex-1 flex items-center justify-center"><p className="text-[#6B7280]">Selecione um local</p></div>
          ) : !recognizedEmployee ? (
            <div className="flex flex-col gap-4 flex-1">
              <PinLoginPanel pinInput={pinInput} setPinInput={setPinInput} onLogin={handlePinLogin} error={pinError} />
            </div>
          ) : (
            <ActionPanel currentRecord={currentRecord} actionButtons={actionButtons} onAction={recordAction} onReset={resetKiosk} geoStatus={geoStatus} />
          )}
        </div>
      </div>

      {/* RGPD Dialog */}
      <Dialog open={rgpdDialogOpen} onOpenChange={setRgpdDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-black flex items-center gap-2" style={{ fontFamily: 'Satoshi, sans-serif' }}><Shield className="w-6 h-6" /> Consentimento RGPD</DialogTitle></DialogHeader>
          <div className="py-4">
            <div className="bg-[#F3F4F6] border border-[#E5E7EB] p-6 max-h-[50vh] overflow-y-auto"><pre className="whitespace-pre-wrap text-sm leading-relaxed font-mono">{rgpdText}</pre></div>
            <div className="mt-4 p-4 bg-white border border-[#E5E7EB]">
              <p className="text-sm"><strong>Funcionário:</strong> {pendingEmployee?.name}</p>
              <p className="text-sm"><strong>Email:</strong> {pendingEmployee?.email}</p>
              <p className="text-sm"><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t border-[#E5E7EB]">
            <Button variant="outline" onClick={printRgpd} className="mr-auto" data-testid="rgpd-print-btn"><Printer className="w-4 h-4 mr-2" /> Imprimir</Button>
            <Button variant="outline" onClick={() => { setRgpdDialogOpen(false); setPendingEmployee(null); }}>Recusar</Button>
            <Button onClick={acceptRgpd} className="bg-[#059669] hover:bg-[#059669]/90" data-testid="rgpd-accept-btn">Aceito e Consinto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
